import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAdminSquads, useCreateAdminSquad, useDeleteAdminSquad, useUpdateAdminSquad, } from '@/api/opengater/useLocations';
import { Input } from '@/components/primitives/Input';
import { Toggle } from '@/components/primitives/Toggle';
import { PanelForm, SaveBar, SettingsAction, SettingsDanger, SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import { buildChangedPatch, locationAdminError, locationConfirmationError, locationFieldErrors, requiredTextError, } from './locationRules';
import { createdEntityId } from './creationResult';
const empty = { name: '', description: '', is_active: true };
const draftOf = (squad) => ({
    name: squad.name,
    description: squad.description ?? '',
    is_active: squad.is_active,
});
const normalized = (draft) => ({
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    is_active: draft.is_active,
});
export function SquadForm({ squadId, creating = false, onDirtyChange, onCreated, onDone, }) {
    const squads = useAdminSquads('all');
    const squad = squads.data?.find((row) => row.id === squadId) ?? null;
    const create = useCreateAdminSquad();
    const update = useUpdateAdminSquad();
    const remove = useDeleteAdminSquad();
    const [base, setBase] = useState(empty);
    const [draft, setDraft] = useState(empty);
    const [fieldErrors, setFieldErrors] = useState({});
    const [confirmationError, setConfirmationError] = useState(null);
    const dirtyRef = useRef(false);
    useEffect(() => {
        if (creating || !squad || dirtyRef.current)
            return;
        const next = draftOf(squad);
        setBase(next);
        setDraft(next);
    }, [creating, squad]);
    useEffect(() => {
        if (!creating && squads.data && !squad && !dirtyRef.current)
            onDone();
    }, [creating, onDone, squad, squads.data]);
    const patch = useMemo(() => buildChangedPatch(normalized(base), normalized(draft), ['description']), [base, draft]);
    const dirty = creating
        ? Object.values(draft).some((value) => value !== '' && value !== true)
        : Object.keys(patch).length > 0;
    useEffect(() => {
        dirtyRef.current = dirty;
    }, [dirty]);
    const nameError = requiredTextError(draft.name) ?? fieldErrors.name;
    const saving = create.isPending || update.isPending;
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
    if (!creating && squads.isLoading)
        return _jsx(SettingsLoading, {});
    if (!creating && ((squads.error && !squads.data) || !squad)) {
        return (_jsx(SettingsNote, { tone: "error", children: locationAdminError(squads.error, 'Сквад не найден.') }));
    }
    const submit = async (event) => {
        event.preventDefault();
        if (nameError || saving || (!creating && !dirty))
            return;
        try {
            if (creating) {
                const beforeIds = (squads.data ?? []).map((row) => row.id);
                const response = await create.mutateAsync(normalized(draft));
                let id = createdEntityId(response, beforeIds, []);
                try {
                    const refreshed = await squads.refetch({ throwOnError: true });
                    id = createdEntityId(response, beforeIds, (refreshed.data ?? []).map((row) => row.id));
                }
                catch {
                    // The POST succeeded. Catalog refresh can recover independently.
                }
                onCreated(id);
            }
            else if (squad) {
                await update.mutateAsync({ id: squad.id, patch });
                const refreshed = await squads.refetch({ throwOnError: true });
                const authoritative = refreshed.data?.find((row) => row.id === squad.id);
                if (!authoritative)
                    throw new Error('Saved squad was not returned');
                const next = draftOf(authoritative);
                setBase(next);
                setDraft(next);
            }
            setFieldErrors({});
            setConfirmationError(null);
        }
        catch (error) {
            const fields = locationFieldErrors(error, ['name', 'description', 'is_active']);
            setFieldErrors(fields);
            if (Object.keys(fields).length === 0) {
                setConfirmationError(locationConfirmationError(error));
            }
            else {
                setConfirmationError(null);
            }
        }
    };
    const saveError = create.error ?? update.error;
    const visibleSaveError = confirmationError ?? (saveError ? locationAdminError(saveError) : null);
    return (_jsxs(PanelForm, { meta: creating ? 'Новый сквад' : `Сквад #${squad.id}`, onSubmit: submit, children: [_jsxs(SettingsGroup, { title: creating ? 'Новый сквад' : 'Сквад', children: [_jsx(SettingsRow, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", wide: true, error: nameError, children: _jsx(Input, { "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0441\u043A\u0432\u0430\u0434\u0430", value: draft.name, disabled: saving, onChange: (event) => {
                                setDraft({ ...draft, name: event.target.value });
                                setFieldErrors((current) => ({ ...current, name: undefined }));
                                setConfirmationError(null);
                                update.reset();
                            } }) }), _jsx(SettingsRow, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", wide: true, error: fieldErrors.description, children: _jsx(Input, { "aria-label": "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u0441\u043A\u0432\u0430\u0434\u0430", value: draft.description, disabled: saving, onChange: (event) => {
                                setDraft({ ...draft, description: event.target.value });
                                setFieldErrors((current) => ({ ...current, description: undefined }));
                                setConfirmationError(null);
                                update.reset();
                            } }) }), creating && (_jsx(SettingsRow, { label: "\u0410\u043A\u0442\u0438\u0432\u0435\u043D", error: fieldErrors.is_active, children: _jsx(Toggle, { "aria-label": "\u0410\u043A\u0442\u0438\u0432\u0435\u043D", on: draft.is_active, disabled: saving, onChange: (value) => {
                                setDraft({ ...draft, is_active: value });
                                setFieldErrors((current) => ({ ...current, is_active: undefined }));
                                setConfirmationError(null);
                                create.reset();
                            } }) }))] }), !creating && (_jsxs(_Fragment, { children: [squad.is_active && (_jsx(SettingsAction, { label: "\u0421\u043A\u0440\u044B\u0442\u044C \u0441\u043A\u0432\u0430\u0434", icon: "flag", tone: "danger", confirmText: "\u0421\u043A\u0440\u044B\u0442\u0438\u0435 \u043E\u0441\u0442\u0430\u0432\u0438\u0442 \u0441\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u044E\u0449\u0438\u0435 \u0441\u0432\u044F\u0437\u0438. \u041D\u043E\u0432\u044B\u0435 \u043D\u0430\u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u0447\u0435\u0440\u0435\u0437 \u044D\u0442\u043E\u0442 \u0441\u043A\u0432\u0430\u0434 \u043D\u0435 \u043E\u0431\u0435\u0449\u0430\u044E\u0442\u0441\u044F.", confirmLabel: "\u0421\u043A\u0440\u044B\u0442\u044C", pending: update.isPending, disabled: dirty, error: update.error ? locationAdminError(update.error) : undefined, onConfirm: async () => {
                            try {
                                await update.mutateAsync({ id: squad.id, patch: { is_active: false } });
                                await squads.refetch({ throwOnError: true });
                            }
                            catch {
                                // The mutation exposes its error below the action.
                            }
                        } })), _jsx(SettingsNote, { tone: "warn", children: "\u0423\u0434\u0430\u043B\u0435\u043D\u0438\u0435 \u0437\u0430\u043F\u0438\u0441\u0438 \u043D\u0435 \u0443\u0434\u0430\u043B\u044F\u0435\u0442 \u0441\u043A\u0432\u0430\u0434 \u0438\u0437 VPN-\u043F\u0430\u043D\u0435\u043B\u0438 \u0438 \u043D\u0435 \u0433\u0430\u0440\u0430\u043D\u0442\u0438\u0440\u0443\u0435\u0442 \u043E\u0442\u0441\u0443\u0442\u0441\u0442\u0432\u0438\u0435 \u0434\u0440\u0443\u0433\u0438\u0445 \u043F\u0440\u0438\u0432\u044F\u0437\u043E\u043A." }), _jsx(SettingsDanger, { label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0441\u043A\u0432\u0430\u0434", confirmText: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0437\u0430\u043F\u0438\u0441\u044C \u0441\u043A\u0432\u0430\u0434\u0430? \u0421\u0432\u044F\u0437\u0430\u043D\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435 \u0432 VPN-\u043F\u0430\u043D\u0435\u043B\u0438 \u043E\u0441\u0442\u0430\u043D\u0443\u0442\u0441\u044F.", pending: remove.isPending, disabled: dirty, error: remove.error ? locationAdminError(remove.error) : undefined, onConfirm: () => remove.mutate(squad.id, { onSuccess: onDone }) })] })), visibleSaveError && _jsx(SettingsNote, { tone: "error", children: visibleSaveError }), _jsx(SaveBar, { dirty: creating || dirty, saving: saving, saved: update.isSuccess, canSave: !nameError && !saving && (creating || dirty), error: visibleSaveError ?? undefined, onReset: creating
                    ? onDone
                    : () => {
                        setDraft(base);
                        setFieldErrors({});
                        setConfirmationError(null);
                        update.reset();
                    }, text: creating ? 'Новый сквад' : undefined, submitLabel: creating ? 'Создать' : undefined })] }));
}
